/**
 * Clip tool.
 *
 * Cuts a polygon or line layer down to a boundary layer's polygons, and
 * drops the result in as a new temporary layer — the same destination
 * Buffer's own result lands in.
 *
 * Polygon-on-polygon is a straight `@turf/intersect`. A line has no such
 * primitive in the turf family, so it is done in two steps: `@turf/line-split`
 * cuts the line at every point it crosses the boundary (an unsplit line —
 * nothing in `features`, because nothing crossed — falls back to testing the
 * line whole), then each resulting piece is kept or dropped by testing ONE
 * of its own interior points with `@turf/boolean-point-in-polygon` — safe
 * because a piece between two crossings (or a piece that never crossed at
 * all) cannot itself straddle the boundary again.
 */

import { useState } from 'react';
import { Loader2, Scissors } from 'lucide-react';
import ToolPanelHeader, { ToolActions } from '../ToolPanel';
import { scopedLayers, type ToolContext } from './types';
import { EmptyHint, Field, LayerSelect, Notice, btnClass } from './shared';
import { getLayerDisplayName } from '../../../layers/capabilities';
import { loadFeaturesDetailed } from './featureLoad';
import type { LayerRenderer } from '../../../types';
import turfUnion from '@turf/union';
import turfIntersect from '@turf/intersect';
import turfLineSplit from '@turf/line-split';
import { booleanPointInPolygon } from '@turf/boolean-point-in-polygon';

const MAX_FEATURES = 5000;
type SourceMode = 'selected' | 'all';
type BoundaryPolygon = GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>;

/** A point safely inside one piece of a split (or never-split) line — the
 *  midpoint of its own middle pair of vertices for an even-length chain, or
 *  the actual middle vertex for an odd one. Never an endpoint: an endpoint
 *  of a split piece can sit exactly ON the boundary, where "inside" is
 *  ambiguous. */
function interiorPointOf(coords: GeoJSON.Position[]): [number, number] {
  const midIndex = Math.floor((coords.length - 1) / 2);
  if (coords.length <= 2 || coords.length % 2 === 0) {
    const a = coords[midIndex];
    const b = coords[midIndex + 1] ?? a;
    return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  }
  return [coords[midIndex][0], coords[midIndex][1]];
}

/** Every piece of `line` that lies inside `boundary`, each as its own
 *  LineString — the actual clip, for one LineString part. */
function clipLine(
  line: GeoJSON.Feature<GeoJSON.LineString>,
  boundary: BoundaryPolygon,
): GeoJSON.LineString[] {
  let pieces: GeoJSON.Feature<GeoJSON.LineString>[];
  try {
    const split = turfLineSplit(line, boundary);
    // lineSplit returns NO features (not the line unchanged) when nothing
    // crossed — the line is then either entirely inside or entirely
    // outside, and still has to be tested as one whole piece.
    pieces = split.features.length > 0 ? split.features : [line];
  } catch {
    pieces = [line];
  }

  const kept: GeoJSON.LineString[] = [];
  for (const piece of pieces) {
    const coords = piece.geometry.coordinates;
    if (coords.length < 2) continue;
    if (booleanPointInPolygon(interiorPointOf(coords), boundary)) kept.push(piece.geometry);
  }
  return kept;
}

export function ClipPanel({ ctx }: { ctx: ToolContext }) {
  const allowed = scopedLayers(ctx.config, ctx.layers);
  const inputCandidates = allowed.filter(
    (layer) => layer.geometryType === 'line' || layer.geometryType === 'polygon',
  );
  const boundaryCandidates = ctx.layers.filter((layer) => layer.geometryType === 'polygon');

  const [inputLayerId, setInputLayerId] = useState(inputCandidates[0]?.id ?? '');
  const [inputMode, setInputMode] = useState<SourceMode>('all');
  const [boundaryLayerId, setBoundaryLayerId] = useState(boundaryCandidates[0]?.id ?? '');
  const [boundaryMode, setBoundaryMode] = useState<SourceMode>('all');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ input: number; output: number; dropped: number } | null>(null);

  const inputLayer = inputCandidates.find((layer) => layer.id === inputLayerId);
  const boundaryLayer = boundaryCandidates.find((layer) => layer.id === boundaryLayerId);

  const run = async () => {
    if (!inputLayer || !boundaryLayer) return;
    setError(null);
    setSummary(null);
    setBusy(true);
    try {
      /* ── The boundary: every chosen polygon feature, unioned into one. ── */
      const boundaryFeatures =
        boundaryMode === 'selected'
          ? (ctx.getSelection?.() ?? [])
              .filter((row) => row.layerId === boundaryLayer.id && row.geometry)
              .map((row) => ({
                type: 'Feature' as const,
                properties: row.properties ?? {},
                geometry: row.geometry as GeoJSON.Geometry,
              }))
          : (await loadFeaturesDetailed(boundaryLayer, ctx, { limit: MAX_FEATURES })).features;

      const boundaryPolygons = boundaryFeatures.filter(
        (feature): feature is BoundaryPolygon =>
          feature.geometry?.type === 'Polygon' || feature.geometry?.type === 'MultiPolygon',
      );
      if (boundaryPolygons.length === 0) {
        setError(
          boundaryMode === 'selected'
            ? 'No polygon features are currently selected on the clip layer.'
            : 'The clip layer has no polygon features to clip against.',
        );
        return;
      }

      let boundary: BoundaryPolygon;
      if (boundaryPolygons.length === 1) {
        boundary = boundaryPolygons[0];
      } else {
        const merged = turfUnion({ type: 'FeatureCollection', features: boundaryPolygons });
        if (!merged) {
          setError('Could not combine the clip boundary features into one shape.');
          return;
        }
        boundary = merged;
      }

      /* ── The input: every feature to cut down to that boundary. ── */
      const inputFeatures =
        inputMode === 'selected'
          ? (ctx.getSelection?.() ?? [])
              .filter((row) => row.layerId === inputLayer.id && row.geometry)
              .map((row) => ({
                type: 'Feature' as const,
                properties: row.properties ?? {},
                geometry: row.geometry as GeoJSON.Geometry,
              }))
          : (await loadFeaturesDetailed(inputLayer, ctx, { limit: MAX_FEATURES })).features;

      if (inputFeatures.length === 0) {
        setError(
          inputMode === 'selected'
            ? 'No features are currently selected on the layer to clip.'
            : 'That layer returned no features.',
        );
        return;
      }

      /* ── Clip each input feature, by its own geometry type. ── */
      const outputFeatures: GeoJSON.Feature[] = [];
      let dropped = 0;

      for (const feature of inputFeatures) {
        const geometry = feature.geometry;
        const properties = feature.properties ?? {};

        if (geometry?.type === 'Polygon' || geometry?.type === 'MultiPolygon') {
          try {
            const clipped = turfIntersect({
              type: 'FeatureCollection',
              features: [{ type: 'Feature', properties: {}, geometry }, boundary],
            });
            if (clipped) outputFeatures.push({ type: 'Feature', properties, geometry: clipped.geometry });
            else dropped += 1;
          } catch {
            dropped += 1;
          }
          continue;
        }

        if (geometry?.type === 'LineString' || geometry?.type === 'MultiLineString') {
          const parts: GeoJSON.Feature<GeoJSON.LineString>[] =
            geometry.type === 'LineString'
              ? [{ type: 'Feature', properties: {}, geometry }]
              : geometry.coordinates.map((coordinates) => ({
                  type: 'Feature' as const,
                  properties: {},
                  geometry: { type: 'LineString' as const, coordinates },
                }));

          const kept = parts.flatMap((part) => clipLine(part, boundary));
          if (kept.length === 0) {
            dropped += 1;
          } else if (kept.length === 1) {
            outputFeatures.push({ type: 'Feature', properties, geometry: kept[0] });
          } else {
            outputFeatures.push({
              type: 'Feature',
              properties,
              geometry: { type: 'MultiLineString', coordinates: kept.map((line) => line.coordinates) },
            });
          }
          continue;
        }

        dropped += 1;
      }

      if (outputFeatures.length === 0) {
        setError('Nothing from that layer falls inside the clip boundary.');
        return;
      }

      const renderer: LayerRenderer = {
        type: 'single',
        color: '#7C3AED',
        fillOpacity: 0.25,
        outlineColor: '#5B21B6',
        outlineWidth: 1.5,
        pointSize: 8,
      };
      ctx.onCreateTempLayer?.(
        `Clip — ${getLayerDisplayName(inputLayer)}`,
        { type: 'FeatureCollection', features: outputFeatures },
        'Clip',
        renderer,
      );
      setSummary({ input: inputFeatures.length, output: outputFeatures.length, dropped });
    } catch {
      setError('Could not clip these features — try again.');
    } finally {
      setBusy(false);
    }
  };

  const ModeToggle = ({
    value,
    onChange,
    firstLabel,
    secondLabel,
  }: {
    value: SourceMode;
    onChange: (mode: SourceMode) => void;
    firstLabel: string;
    secondLabel: string;
  }) => (
    <div className="flex gap-1 rounded-md bg-slate-100 p-0.5 text-[11px] font-medium">
      <button
        type="button"
        onClick={() => onChange('all')}
        className={`flex-1 rounded px-2 py-1 transition ${
          value === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
        }`}
      >
        {firstLabel}
      </button>
      <button
        type="button"
        onClick={() => onChange('selected')}
        className={`flex-1 rounded px-2 py-1 transition ${
          value === 'selected' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
        }`}
      >
        {secondLabel}
      </button>
    </div>
  );

  return (
    <>
      <ToolPanelHeader
        title="Clip"
        description="Cut a polygon or line layer down to a boundary layer's polygons."
        onClose={ctx.onClose}
      />

      <Field label="Layer to clip">
        <LayerSelect layers={inputCandidates} value={inputLayerId} onChange={setInputLayerId} />
      </Field>
      {inputCandidates.length === 0 ? (
        <EmptyHint>No line or polygon layers are available to clip.</EmptyHint>
      ) : (
        <ModeToggle
          value={inputMode}
          onChange={setInputMode}
          firstLabel="All features"
          secondLabel="From selected features"
        />
      )}

      <Field label="Clip boundary" hint="A polygon layer — every feature it holds is combined into one boundary.">
        <LayerSelect layers={boundaryCandidates} value={boundaryLayerId} onChange={setBoundaryLayerId} />
      </Field>
      {boundaryCandidates.length === 0 ? (
        <EmptyHint>No polygon layers are available to clip against.</EmptyHint>
      ) : (
        <ModeToggle
          value={boundaryMode}
          onChange={setBoundaryMode}
          firstLabel="All boundary features"
          secondLabel="From selected boundary features"
        />
      )}

      {error && <Notice tone="warn">{error}</Notice>}

      {summary && (
        <Notice>
          Clipped {summary.input} feature{summary.input === 1 ? '' : 's'} → {summary.output} piece
          {summary.output === 1 ? '' : 's'} kept
          {summary.dropped > 0
            ? `, ${summary.dropped} entirely outside the boundary`
            : ''}
          .
        </Notice>
      )}

      <ToolActions>
        <button
          type="button"
          onClick={() => void run()}
          disabled={!inputLayer || !boundaryLayer || busy}
          className={`${btnClass} gl-btn--primary`}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Scissors className="h-3.5 w-3.5" />}
          {busy ? 'Clipping…' : 'Clip'}
        </button>
      </ToolActions>
    </>
  );
}
